// src/AdditionalFeaturePage.js
import React, { useState } from 'react';
import { dedupeChecklist } from './utils/dedupeChecklist';

export default function AdditionalFeaturePage({ features }) {
  const colorBlue  = '#153BC6';
  const lightGray1 = '#f7f7f7';
  const lightGray2 = '#eaeaea';

  // State for the image modal
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [zoomed, setZoomed]               = useState(false);

  // Scroll to the “Additional Features” section of the Damage Report
  function scrollToDamageReport() {
    const target =
      document.getElementById('damage-additional-feature')    ||
      document.getElementById('damage-additional-features')   ||
      document.getElementById('damage-additionalfeature')     ||
      document.getElementById('damage-additionalfeatures')    ||
      document.getElementById('damage-report');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // Render a red “X” if damaged, green “✓” if not
  function renderDamageIcon(isDamaged) {
    return isDamaged ? (
      <span style={{ color: 'red', fontWeight: 'bold' }}>&#10007;</span>
    ) : (
      <span style={{ color: 'green', fontWeight: 'bold' }}>&#10003;</span>
    );
  }

  // Modal handlers
  function openModal(url) {
    setSelectedImageUrl(url);
    setZoomed(false);
  }
  function closeModal() {
    setSelectedImageUrl(null);
    setZoomed(false);
  }
  function toggleZoom() {
    setZoomed((prev) => !prev);
  }

  // Build a deduplicated list of feature names + damage flags
  const uniqueFeatures = Object.values(
    features.reduce((acc, f) => {
      const name = f.AdditionalFeatureName;
      if (!acc[name]) {
        acc[name] = { name, isDamaged: f.AdditionalFeatureIsDamaged };
      } else {
        acc[name].isDamaged = acc[name].isDamaged || f.AdditionalFeatureIsDamaged;
      }
      return acc;
    }, {})
  );

  // Gather all feature images
  const imagesList = features
    .filter((f) => !!f.AdditionalFeatureImage)
    .map((f) => f.AdditionalFeatureImage);

  return (
    <div
      className="report-page"
      style={{
        width: '8.5in',
        minHeight: '11in',
        margin: '0 auto',
        padding: '0.3in',
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        boxSizing: 'border-box',
        fontFamily: 'Arial, sans-serif',
        position: 'relative',
      }}
    >
      <h2 style={{ marginBottom: '0.2in', color: colorBlue }}>
        Additional Features
      </h2>

      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
        {/* ------------ Checklist & Climate Column ------------ */}
        <div style={{ flex: '0 0 50%' }}>
          {/* Checklist */}
          <div style={{ borderLeft: `5px solid ${colorBlue}`, paddingLeft: 8, marginBottom: '0.2in' }}>
            <h3 style={{ color: colorBlue, marginBottom: 4, fontSize: '1rem' }}>
              Additional Feature Checklist
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {uniqueFeatures.map((item, idx) => {
                  const bgColor = idx % 2 === 0 ? lightGray1 : lightGray2;
                  return (
                    <tr key={item.name} style={{ backgroundColor: bgColor }}>
                      <td style={{ padding: '4px 8px', verticalAlign: 'middle' }}>
                        {item.name}
                      </td>
                      <td
                        style={{
                          padding: '4px 8px',
                          textAlign: 'center',
                          verticalAlign: 'middle',
                          cursor: item.isDamaged ? 'pointer' : 'default',
                        }}
                        onClick={item.isDamaged ? scrollToDamageReport : undefined}
                        title={item.isDamaged ? 'Jump to Damage Report' : undefined}
                      >
                        {renderDamageIcon(item.isDamaged)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Climate Control Tests */}
          <div
            style={{
              borderLeft: `5px solid ${colorBlue}`,
              paddingLeft: 8,
              backgroundColor: '#f9f9f9',
              padding: '10px',
            }}
          >
            <h3 style={{ color: colorBlue, marginBottom: 6, fontSize: '1rem' }}>
              Climate Control Tests
            </h3>
            <p style={{ margin: '6px 0' }}>
              We test the heating and air conditioning systems by measuring
              the temperature coming directly from the vents using a
              thermometer.
            </p>
            <p style={{ margin: '4px 0 6px 20px' }}>
              A/C should blow as cold as 40–50°F<br />
              Heat should reach 110–150°F depending on outside conditions
            </p>
            <p style={{ margin: '6px 0' }}>
              If the system doesn’t hit typical ranges or doesn’t respond
              correctly, we flag it in the report—whether it’s blowing
              lukewarm or not working at all, you’ll know.
            </p>
          </div>
        </div>

        {/* ------------ Images Column ------------ */}
        <div style={{ flex: '0 0 50%' }}>
          <div
            style={{
              borderLeft: `5px solid ${colorBlue}`,
              paddingLeft: 8,
              marginBottom: '0.2in',
            }}
          >
            <h3 style={{ color: colorBlue, marginBottom: 4, fontSize: '1rem' }}>
              Feature Images
            </h3>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                rowGap: '4px',
                columnGap: '8px',
                alignItems: 'flex-start',
                alignContent: 'flex-start',
              }}
            >
              {imagesList.map((url, idx) => (
                <div
                  key={idx}
                  style={{
                    width: '80px',
                    height: '80px',
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    overflow: 'hidden',
                    cursor: 'pointer',
                  }}
                  onClick={() => openModal(url)}
                >
                  <img
                    src={url}
                    alt="Feature"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Logo */}
      <div style={{ position: 'absolute', bottom: 10, right: 10 }}>
        <img src="/logo.png" alt="Company Logo" style={{ height: 40 }} />
      </div>

      {/* Image Modal */}
      {selectedImageUrl && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={closeModal}
        >
          <img
            src={selectedImageUrl}
            alt="Enlarged"
            onClick={(e) => {
              e.stopPropagation();
              toggleZoom();
            }}
            style={{
              maxWidth: zoomed ? '95%' : '50%',
              maxHeight: zoomed ? '95%' : '60%',
              border: '2px solid #fff',
              borderRadius: 6,
              cursor: zoomed ? 'zoom-out' : 'zoom-in',
              transition: 'all 0.3s ease',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: 10,
              right: 20,
              color: 'white',
              fontSize: '2rem',
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
            onClick={closeModal}
          >
            &times;
          </div>
        </div>
      )}
    </div>
  );
}
